import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { benefitsService } from '../services/benefits';
import { useReferido } from '../hooks/useReferido';
import { toast } from '../hooks/use-toast';
import { FlipCard } from '../components/ui/FlipCard';

/** Dónde queda apuntado el beneficio elegido mientras el visitante crea su cuenta. */
export const BENEFICIO_ELEGIDO = 'vondi_beneficio_elegido';

/**
 * `LOGIN_CANDADOS_SELECCIONBENEFICIO` — «Elige dónde vas a usar tu beneficio».
 *
 * También es pública: el visitante todavía no tiene cuenta. Elige primero y da
 * sus datos después, cuando ya sabe qué se lleva.
 *
 * El elegido se ve a todo color y los demás quedan atenuados con candado, para
 * que en todo momento sea evidente que es **uno solo**.
 *
 * La elección se guarda en `sessionStorage` y no en la URL: si el visitante
 * comparte el enlace del registro, el otro no hereda su selección. Y al ser de
 * sesión, no queda rondando en el navegador después de cerrar.
 */
export default function ElegirBeneficio() {
  const navigate = useNavigate();
  const [beneficios, setBeneficios] = useState([]);
  const [elegido, setElegido] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [procesando, setProcesando] = useState(false);
  /* Esta pantalla ES el desbloqueo del gratis, así que sin referido no se
     entra ni escribiendo la URL a mano (Iván, 30-ago-2026). */
  const referido = useReferido();

  useEffect(() => {
    let vigente = true;
    benefitsService
      .getAll()
      .then((r) => {
        if (!vigente) return;
        const lista = r?.data ?? [];
        setBeneficios(lista);
        // Se preselecciona el primero para que el botón nunca se vea inerte.
        if (lista.length) setElegido(String(lista[0].id));
      })
      .catch(() => {})
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, []);

  /*
    Continuar tiene dos caminos, según si ya hay sesión.

    El visitante sin cuenta pasa por el registro, que es el flujo principal.
    Pero quien YA entró alguna vez y vuelve a caer aquí desde el landing —cosa
    normal, la sesión dura— no puede ir al registro: `PublicRoute` lo rebota a
    /home y, si ya gastó su bienvenida, lo primero que toca le pide pagar. Eso
    se veía como «me pide pagar antes de canjear», y solo desaparecía en
    incógnito.

    Con sesión, entonces, se desbloquea aquí mismo y se le manda a su QR.
  */
  const continuar = async () => {
    if (!elegido || procesando) return;

    const haySesion =
      typeof window !== 'undefined' &&
      localStorage.getItem('auth_token') &&
      localStorage.getItem('isLoggedIn') === 'true';

    if (!haySesion) {
      sessionStorage.setItem(BENEFICIO_ELEGIDO, elegido);
      navigate('/signup');
      return;
    }

    setProcesando(true);
    try {
      await benefitsService.unlock(elegido);
      toast({
        title: '¡Beneficio desbloqueado!',
        description: 'Muestra tu QR en tienda para usarlo.',
      });
      navigate('/home?tab=qr');
    } catch (err) {
      // Ya gastó su beneficio de bienvenida: ahí sí toca el muro.
      const codigo = err?.data?.code;
      if (err?.status === 402 || codigo === 'PAYWALL') {
        navigate('/subscription', { state: { from: 'paywall' } });
        return;
      }
      toast({
        title: 'No se pudo desbloquear',
        description: err?.message || 'Inténtalo de nuevo en un momento.',
        variant: 'destructive',
      });
      setProcesando(false);
    }
  };

  /* Sin referido no hay beneficio de bienvenida que elegir: a la compra. Se
     espera a que el código termine de validarse (`null`) antes de decidir, para
     no rebotar a quien sí traía el suyo. */
  if (referido === false) return <Navigate to="/subscription" replace />;
  if (referido === null) return null;

  return (
    <div style={{ minHeight: '100vh', background: '#fff', paddingBottom: 130 }}>
      <div style={{ padding: 'max(58px, env(safe-area-inset-top, 58px)) 24px 0' }}>
        <h1
          className="font-display"
          style={{
            fontSize: 'clamp(24px, 7vw, 31px)',
            fontWeight: 700,
            letterSpacing: '-0.025em',
            color: '#0E0E10',
            margin: 0,
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          Elige dónde vas a usar
          <br />
          tu beneficio de bienvenida
        </h1>

        <p
          style={{
            fontSize: 14,
            color: '#6E6E77',
            textAlign: 'center',
            lineHeight: 1.5,
            margin: '14px 0 26px',
          }}
        >
          Puedes desbloquear gratis cualquiera de los beneficios de vondi
        </p>

        {cargando && (
          <p style={{ textAlign: 'center', color: '#6E6E77', fontSize: 14 }}>Cargando beneficios...</p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {beneficios.map((b) => {
            const activo = String(b.id) === elegido;
            return (
              <div
                key={b.id}
                style={{
                  transition: 'transform .2s ease',
                  transform: activo ? 'scale(1)' : 'scale(0.94)',
                }}
              >
                <FlipCard
                  benefit={b}
                  estado={activo ? 'gratis' : 'bloqueada'}
                  onClick={() => setElegido(String(b.id))}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* El botón vive fijo abajo: la lista puede ser larga y no queremos que
          haya que hacer scroll hasta el final para poder continuar. */}
      <div
        style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          padding: '16px 24px calc(16px + env(safe-area-inset-bottom, 0px))',
          background: 'linear-gradient(to top, #fff 62%, rgba(255,255,255,0))',
        }}
      >
        <button
          type="button"
          onClick={continuar}
          disabled={!elegido || procesando}
          className="bg-grad-rojo-rosa text-white rounded-full font-semibold"
          style={{
            width: '100%',
            height: 54,
            border: 'none',
            fontSize: 15,
            letterSpacing: '0.01em',
            cursor: elegido && !procesando ? 'pointer' : 'not-allowed',
            opacity: elegido && !procesando ? 1 : 0.5,
            boxShadow: '0 12px 28px -10px rgba(245,43,22,0.55)',
          }}
        >
          {procesando ? 'DESBLOQUEANDO...' : '¡DESBLOQUEAR BENEFICIO!'}
        </button>
      </div>
    </div>
  );
}
